/**
 * 配信先（Issue #735 → #793 → #800）: 機器（TetherCam の「シンクロ連携」）に入れる**ストリームキー付きの制御 URL** 1 つだけを出す。
 * 「撮影」の撮影予定のページに置く。SRT の受け口の番号は補正サーバーが機器に stream_target で教えるので出さない（#793）。
 * ストリームキーは本番が 1 本持ち（GetLiveAccess）、SRT のパスフレーズと機器 → 補正サーバーの Bearer の両方に使う。
 * 既定は伏せ字、「表示」で見せる、「コピー」、「作り直す」（RegenerateLiveStreamKey。配信中なら一度切れる）。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';

export const MASK = '••••••••';

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* 下へ */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand('copy');
    ta.remove();
    return !!ok;
  } catch {
    return false;
  }
}

export default function LiveAccessPanel() {
  const [access, setAccess] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setAccess(await api.project('GetLiveAccess', {}));
      setError(null);
    } catch (e) {
      setError(String(e?.message ?? e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    if (!window.confirm('ストリームキーを作り直します。配信中なら一度切れ、OBS・TetherCam の設定を新しいキーに入れ直すまで繋がりません。よいですか？')) return;
    setBusy(true);
    try {
      setAccess(await api.project('RegenerateLiveStreamKey', {}));
      setShown(true);
      setError(null);
    } catch (e) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="live-access-panel">
      <div className="card-title" style={{ marginBottom: 8 }}>配信先</div>
      {access === null ? <div className="card-meta">{error ? `読めません（${error}）` : '読んでいます…'}</div> : (
        <>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
            <tbody>
              <tr data-testid="live-access-control-link">
                <td style={{ padding: '4px 8px 4px 0', whiteSpace: 'nowrap', color: '#a0a0c0' }}>制御 URL</td>
                <td style={{ padding: '4px 8px 4px 0', fontFamily: 'monospace', wordBreak: 'break-all' }} data-testid="live-access-link-value">
                  {shown ? access.discover_link : `${access.discover_link.split('?')[0]}?key=${MASK}`}
                </td>
                <td style={{ padding: '4px 0', whiteSpace: 'nowrap' }}>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShown((v) => !v)} style={{ marginRight: 4 }}>
                    {shown ? '隠す' : '表示'}
                  </button>
                  <CopyButton value={access.discover_link} />
                  <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={regenerate} style={{ marginLeft: 4 }}>作り直す</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="card-meta" style={{ marginTop: 8 }}>
            TetherCam の「シンクロ連携」にこの URL を入れる（本番の URL。今の補正サーバー（EC2・家・会場）は TetherCam が本番に聞く。鍵はストリームキー。配信先の番号は補正サーバーが教える）。
            {access.created_at ? <span>（作った日時 {access.created_at}）</span> : null}
          </div>
        </>
      )}
      {error && access !== null ? <div role="status" style={{ color: '#f99', marginTop: 8 }}>{error}</div> : null}
    </div>
  );
}

function CopyButton({ value }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="btn btn-sm btn-secondary" data-testid="live-access-copy"
      onClick={async () => { setDone(await copyText(value ?? '')); setTimeout(() => setDone(false), 1500); }}>
      {done ? 'コピーした' : 'コピー'}
    </button>
  );
}
